import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Grid, TextField, Button, MenuItem, Select, InputLabel, FormControl, FormHelperText, Typography } from '@mui/material';
import FormPage from '../components/FormPage';
import { updateVehicle, fetchVehicles, fetchCustomers } from '../services/ApiService';

const MANUFACTURERS = ["Daewoo", "Mitsubishi", "FAW", "ISUZU", "Hino", "Renault", "Volvo", "DongFeng", "UD", "Toyota", "Other"];
const VEHICLE_TYPES = ["Trailer", "Buses"];

const FIELDS = [
  { label: "Registration No.", name: "registration_number", required: true, placeholder: "e.g. ABC-123" },
  { label: "Model Name", name: "model_name", placeholder: "e.g. Hino 500" },
  { label: "Model Year", name: "model_year", placeholder: "e.g. 2022" },
  { label: "Chassis No.", name: "chassis_number", placeholder: "Vehicle chassis number" },
  { label: "Engine No.", name: "engine_number", placeholder: "Vehicle engine number" },
  { label: "Color", name: "color", placeholder: "e.g. White" },
];

export function EditVehicleForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [haulierOptions, setHaulierOptions] = React.useState({});
  const [formData, setFormData] = React.useState(location.state?.record || null);
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchCustomers().then((res) => {
      setHaulierOptions(res.data.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}));
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!formData) {
      fetchVehicles().then((res) => {
        const found = res.data.find((v) => String(v.id) === String(id));
        if (found) setFormData(found);
        else navigate('/add-truck');
      }).catch(() => navigate('/add-truck'));
    }
  }, [formData, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => navigate('/add-truck');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedData = { ...formData, customer: formData.customer_id || formData.customer };
    delete updatedData.customer_id;
    try {
      await updateVehicle(formData.id, updatedData);
      navigate('/add-truck', { state: { successMessage: 'Vehicle updated successfully' } });
    } catch (error) {
      const errData = error.response?.data;
      if (errData && typeof errData === 'object') setFormErrors(errData);
      else setFormErrors({ general: 'Failed to update vehicle' });
    }
  };

  if (!formData) {
    return (
      <FormPage title="Edit Vehicle" backTo="/add-truck">
        <Typography>Loading...</Typography>
      </FormPage>
    );
  }

  return (
    <FormPage
      title="Edit Vehicle"
      backTo="/add-truck"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Update</Button>
      </>}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "0.75rem" }}>
        Last updated: {formData.updated_at?.slice(0, 19).replace("T", " ")}
      </Typography>

      <Grid container spacing={2}>
        {FIELDS.map((field) => (
          <Grid item xs={12} sm={6} key={field.name}>
            <TextField
              fullWidth label={field.label} name={field.name} placeholder={field.placeholder}
              value={formData[field.name] || ""} onChange={handleChange}
              required={field.required || false} error={!!formErrors[field.name]} helperText={formErrors[field.name]}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={6}>
          <TextField select fullWidth label="Manufacturer" name="manufacturer" value={formData.manufacturer || ""} onChange={handleChange}
            error={!!formErrors.manufacturer} helperText={formErrors.manufacturer} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="" disabled>Select manufacturer</MenuItem>
            {MANUFACTURERS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField select fullWidth label="Vehicle Type" name="vehicle_type" value={formData.vehicle_type || ""} onChange={handleChange}
            error={!!formErrors.vehicle_type} helperText={formErrors.vehicle_type} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="" disabled>Select vehicle type</MenuItem>
            {VEHICLE_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField fullWidth label="Remarks" name="remarks" placeholder="Any additional notes" value={formData.remarks || ""} onChange={handleChange}
            error={!!formErrors.remarks} helperText={formErrors.remarks} />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!formErrors.customer}>
            <InputLabel id="haulier-edit-label">Customer</InputLabel>
            <Select labelId="haulier-edit-label" label="Customer" name="customer_id" value={formData.customer_id || ""} onChange={handleChange} displayEmpty>
              <MenuItem value="" disabled>Select a customer</MenuItem>
              {Object.entries(haulierOptions).map(([key, value]) => <MenuItem key={key} value={key}>{value}</MenuItem>)}
            </Select>
            {formErrors.customer && <FormHelperText>{formErrors.customer}</FormHelperText>}
          </FormControl>
        </Grid>

        {formErrors.general && (
          <Grid item xs={12}>
            <FormHelperText error>{formErrors.general}</FormHelperText>
          </Grid>
        )}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
        Created: {formData.created_at?.slice(0, 19).replace("T", " ")}
      </Typography>
    </FormPage>
  );
}
